<template>
    <div>
        <div class="max-w-6xl mx-auto pb-6 space-y-6">
            <h1 class="text-2xl font-bold text-gray-800">Usuários</h1>

            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <p class="text-sm text-gray-500">{{ users.length }} usuário{{ users.length !== 1 ? 's' : '' }}</p>
                    <p class="text-xs text-gray-400">Cadastre quantos usuários precisar. Usuários com Google não têm senha local.</p>
                </div>
                <button
                    v-if="canManage"
                    @click="openInviteModal"
                    class="btn btn-primary btn-sm"
                >
                    + Convidar usuário
                </button>
            </div>

            <div v-if="loading" class="flex flex-col gap-y-3">
                <div v-for="i in 3" :key="i" class="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-x-4">
                    <div class="skeleton w-10 h-10 rounded-full"></div>
                    <div class="flex-1 space-y-2">
                        <div class="skeleton h-4 w-40 rounded"></div>
                        <div class="skeleton h-3 w-56 rounded"></div>
                    </div>
                </div>
            </div>

            <div v-else class="flex flex-col gap-y-3">
                <div
                    v-for="user in users"
                    :key="user.id"
                    class="bg-white rounded-xl border border-gray-100 p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-x-4"
                >
                    <div class="relative shrink-0">
                        <img
                            v-if="user.photo_url || user.google_avatar_url"
                            :src="user.photo_url || user.google_avatar_url"
                            class="w-10 h-10 rounded-full object-cover"
                        />
                        <div v-else class="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-semibold text-sm">
                            {{ initials(user.name) }}
                        </div>
                        <span
                            v-if="!user.status"
                            class="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white"
                            :class="user.invite_status === 'pending' ? 'bg-yellow-400' : 'bg-gray-300'"
                        ></span>
                        <span
                            v-else
                            class="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white bg-emerald-400"
                        ></span>
                    </div>

                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-x-2 gap-y-1 flex-wrap">
                            <span class="font-medium text-gray-800 text-sm truncate">{{ user.name }}</span>
                            <span v-if="user.uuid === currentUserUUID" class="text-xs text-gray-400">(você)</span>
                            <span :class="roleBadgeClass(user.role)" class="text-xs px-2 py-0.5 rounded-full font-medium">{{ roleLabel(user.role) }}</span>
                            <span v-if="user.invite_status === 'pending'" class="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Convite pendente</span>
                            <span v-else-if="!user.status" class="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Inativo</span>
                            <span
                                v-if="isGoogleUser(user)"
                                class="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full"
                                title="Conta criada com Google"
                            >
                                <GoogleIcon class="w-3.5 h-3.5" />
                                Google
                            </span>
                        </div>
                        <p class="text-xs text-gray-400 mt-0.5 truncate">{{ user.email }}</p>
                    </div>

                    <div v-if="canManage && user.uuid !== currentUserUUID" class="flex flex-wrap items-center gap-2 shrink-0">
                        <button
                            @click="openEditModal(user)"
                            class="text-xs text-gray-500 hover:text-gray-800 border border-gray-200 rounded-md px-2 py-1"
                            title="Editar"
                        >
                            Editar
                        </button>

                        <button
                            v-if="canResetPassword(user)"
                            @click="openResetPasswordModal(user)"
                            class="text-xs text-cyan-600 hover:text-cyan-700 border border-cyan-200 rounded-md px-2 py-1"
                            title="Redefinir senha"
                        >
                            Redefinir senha
                        </button>

                        <button
                            v-if="user.role !== 'owner' && user.invite_status !== 'pending'"
                            @click="toggleStatus(user)"
                            :disabled="togglingId === user.uuid"
                            class="text-xs border rounded-md px-2 py-1"
                            :class="user.status ? 'text-orange-500 border-orange-200 hover:bg-orange-50' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'"
                            :title="user.status ? 'Desativar' : 'Ativar'"
                        >
                            {{ togglingId === user.uuid ? '...' : (user.status ? 'Desativar' : 'Ativar') }}
                        </button>

                        <button
                            v-if="user.role !== 'owner'"
                            @click="confirmDelete(user)"
                            :disabled="deletingId === user.uuid"
                            class="text-xs text-red-500 border border-red-200 hover:bg-red-50 rounded-md px-2 py-1"
                            title="Excluir"
                        >
                            {{ deletingId === user.uuid ? '...' : 'Excluir' }}
                        </button>
                    </div>
                </div>

                <div v-if="!users.length" class="text-center py-12 text-gray-400 text-sm">
                    Nenhum usuário encontrado.
                </div>
            </div>
        </div>

        <div v-if="showInviteModal" class="fixed inset-0 z-50 flex items-center justify-center">
            <div class="absolute inset-0 bg-black opacity-30" @click="closeInviteModal"></div>
            <div class="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4 p-6 z-10">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="font-semibold text-gray-800">Convidar usuário</h3>
                    <button @click="closeInviteModal" class="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                </div>
                <p class="text-sm text-gray-500 mb-4">A pessoa recebe um link por email para criar a própria senha ou entrar com Google.</p>
                <div class="flex flex-col gap-y-3">
                    <div>
                        <label class="text-xs font-medium text-gray-500 block mb-1">Nome</label>
                        <input
                            v-model="inviteForm.name"
                            type="text"
                            placeholder="Nome do usuário"
                            class="input input-bordered w-full text-sm"
                            maxlength="80"
                        />
                    </div>
                    <div>
                        <label class="text-xs font-medium text-gray-500 block mb-1">Email</label>
                        <input
                            v-model="inviteForm.email"
                            type="email"
                            placeholder="email@exemplo.com"
                            class="input input-bordered w-full text-sm"
                            @keyup.enter="sendInvite"
                        />
                    </div>
                    <div>
                        <label class="text-xs font-medium text-gray-500 block mb-1">Permissão</label>
                        <select v-model="inviteForm.role" class="select select-bordered w-full text-sm">
                            <option value="member">Membro</option>
                            <option value="admin">Administrador</option>
                        </select>
                    </div>
                </div>
                <div v-if="inviteError" class="text-red-500 text-xs mt-3">{{ inviteError }}</div>
                <div class="flex justify-end gap-x-2 mt-4">
                    <button @click="closeInviteModal" class="btn btn-ghost btn-sm">Cancelar</button>
                    <button @click="sendInvite" :disabled="inviteSending" class="btn btn-primary btn-sm">
                        {{ inviteSending ? 'Enviando...' : 'Enviar convite' }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="showEditModal && editTarget" class="fixed inset-0 z-50 flex items-center justify-center">
            <div class="absolute inset-0 bg-black opacity-30" @click="closeEditModal"></div>
            <div class="relative bg-white rounded-xl shadow-lg w-full max-w-sm mx-4 p-6 z-10">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="font-semibold text-gray-800">Editar usuário</h3>
                    <button @click="closeEditModal" class="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                </div>
                <div class="flex flex-col gap-y-3">
                    <div>
                        <label class="text-xs font-medium text-gray-500 block mb-1">Nome</label>
                        <input
                            v-model="editForm.name"
                            type="text"
                            class="input input-bordered w-full text-sm"
                            maxlength="80"
                        />
                    </div>
                    <div v-if="editTarget.role !== 'owner'">
                        <label class="text-xs font-medium text-gray-500 block mb-1">Permissão</label>
                        <select v-model="editForm.role" class="select select-bordered w-full text-sm">
                            <option value="member">Membro</option>
                            <option value="admin">Administrador</option>
                        </select>
                    </div>
                    <div v-if="editError" class="text-red-500 text-xs">{{ editError }}</div>
                </div>
                <div class="flex justify-end gap-x-2 mt-4">
                    <button @click="closeEditModal" class="btn btn-ghost btn-sm">Cancelar</button>
                    <button @click="saveEdit" :disabled="editSaving" class="btn btn-primary btn-sm">
                        {{ editSaving ? 'Salvando...' : 'Salvar' }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="showResetPasswordModal && resetTarget" class="fixed inset-0 z-50 flex items-center justify-center">
            <div class="absolute inset-0 bg-black opacity-30" @click="closeResetPasswordModal"></div>
            <div class="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 p-6 z-10">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="font-semibold text-gray-800">Redefinir senha</h3>
                    <button @click="closeResetPasswordModal" class="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                </div>
                <p class="text-sm text-gray-500 mb-4">
                    Nova senha para <strong>{{ resetTarget.name }}</strong>. Contas Google não usam senha local.
                </p>

                <div class="flex flex-col gap-3">
                    <div>
                        <label class="text-xs font-medium text-gray-500 block mb-1">Nova senha</label>
                        <div class="flex flex-col gap-2 sm:flex-row">
                            <input
                                v-model="resetPasswordForm.password"
                                :type="showResetPasswordValue ? 'text' : 'password'"
                                class="input input-bordered w-full text-sm font-mono"
                            />
                            <button type="button" class="btn btn-sm" @click="showResetPasswordValue = !showResetPasswordValue">
                                {{ showResetPasswordValue ? 'Ocultar' : 'Ver' }}
                            </button>
                            <button type="button" class="btn btn-sm" @click="generateResetPassword">Gerar</button>
                            <button type="button" class="btn btn-sm" @click="copyResetPassword">
                                {{ resetPasswordCopied ? 'Copiado!' : 'Copiar' }}
                            </button>
                        </div>
                        <p v-if="resetPasswordErrorText" class="text-red-500 text-xs mt-2">{{ resetPasswordErrorText }}</p>
                        <p v-else class="text-gray-400 text-xs mt-2">Mínimo de 12 caracteres, com maiúscula, minúscula, número e símbolo.</p>
                    </div>

                    <div v-if="resetPasswordDone" class="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700">
                        Senha redefinida com sucesso. Sessões ativas desse usuário foram encerradas.
                    </div>

                    <div v-if="resetPasswordError" class="text-red-500 text-xs">{{ resetPasswordError }}</div>
                </div>

                <div class="flex justify-end gap-x-2 mt-5">
                    <button @click="closeResetPasswordModal" class="btn btn-ghost btn-sm">
                        {{ resetPasswordDone ? 'Fechar' : 'Cancelar' }}
                    </button>
                    <button
                        v-if="!resetPasswordDone"
                        @click="confirmResetPassword"
                        :disabled="resettingPassword || !!resetPasswordErrorText"
                        class="btn btn-primary btn-sm"
                    >
                        {{ resettingPassword ? 'Salvando...' : 'Confirmar' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, defineComponent, h } from 'vue';
import Swal from 'sweetalert';
import AuthService from '../services/AuthService';
import { UserService } from '../services/Entity';
import { generateSecurePassword, validatePasswordStrength } from '@/utils/password';

const GoogleIcon = defineComponent({
    name: 'GoogleIcon',
    setup(_, { attrs }) {
        return () => h('svg', { viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg', ...attrs }, [
            h('path', { d: 'M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z', fill: '#4285F4' }),
            h('path', { d: 'M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z', fill: '#34A853' }),
            h('path', { d: 'M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z', fill: '#FBBC05' }),
            h('path', { d: 'M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z', fill: '#EA4335' })
        ]);
    }
});

export default {
    name: 'Users',
    components: { GoogleIcon },
    setup() {
        const users = ref([]);
        const loading = ref(false);
        const togglingId = ref(null);
        const deletingId = ref(null);

        const showInviteModal = ref(false);
        const inviteSending = ref(false);
        const inviteError = ref('');
        const inviteForm = ref({ name: '', email: '', role: 'member' });

        const showEditModal = ref(false);
        const editTarget = ref(null);
        const editForm = ref({ name: '', role: 'member' });
        const editSaving = ref(false);
        const editError = ref('');

        const showResetPasswordModal = ref(false);
        const resetTarget = ref(null);
        const resetPasswordForm = ref({ password: '' });
        const resettingPassword = ref(false);
        const resetPasswordDone = ref(false);
        const resetPasswordError = ref('');
        const resetPasswordCopied = ref(false);
        const showResetPasswordValue = ref(false);

        const auth = AuthService.getAuthInfo();
        const currentUserUUID = auth ? auth.uuid : null;
        const currentRole = auth ? auth.role : 'member';
        const canManage = computed(() => currentRole === 'owner' || currentRole === 'admin');
        const resetPasswordErrorText = computed(() => validatePasswordStrength(resetPasswordForm.value.password));

        onMounted(fetchUsers);

        async function fetchUsers() {
            try {
                loading.value = true;
                const { data } = await UserService.list();
                users.value = data;
            } catch (err) {
                Swal({ title: 'Erro ao carregar usuários', icon: 'error' });
            } finally {
                loading.value = false;
            }
        }

        function openInviteModal() {
            inviteForm.value = { name: '', email: '', role: 'member' };
            inviteError.value = '';
            showInviteModal.value = true;
        }

        function closeInviteModal() {
            showInviteModal.value = false;
        }

        async function sendInvite() {
            inviteError.value = '';
            const email = inviteForm.value.email.trim().toLowerCase();
            if (!email || !email.includes('@')) {
                inviteError.value = 'Informe um email válido.';
                return;
            }
            try {
                inviteSending.value = true;
                await UserService.invite({
                    email,
                    name: inviteForm.value.name.trim(),
                    role: inviteForm.value.role
                });
                closeInviteModal();
                await fetchUsers();
                Swal({ title: 'Convite enviado!', text: `Email enviado para ${email}`, icon: 'success' });
            } catch (err) {
                const code = err?.response?.data?.error;
                if (code === 'already_exists') {
                    inviteError.value = 'Este email já está cadastrado nesta conta.';
                } else if (code === 'invalid_role') {
                    inviteError.value = 'Permissão inválida.';
                } else {
                    inviteError.value = 'Erro ao enviar convite. Tente novamente.';
                }
            } finally {
                inviteSending.value = false;
            }
        }

        function openEditModal(user) {
            editTarget.value = user;
            editForm.value = { name: user.name || '', role: user.role || 'member' };
            editError.value = '';
            showEditModal.value = true;
        }

        function closeEditModal() {
            showEditModal.value = false;
            editTarget.value = null;
        }

        async function saveEdit() {
            editError.value = '';
            if (!editForm.value.name.trim()) {
                editError.value = 'Nome é obrigatório.';
                return;
            }
            try {
                editSaving.value = true;
                const payload = {
                    uuid: editTarget.value.uuid,
                    name: editForm.value.name.trim()
                };
                if (editTarget.value.role !== 'owner') {
                    payload.role = editForm.value.role;
                }
                const { data } = await UserService.update(payload);
                const idx = users.value.findIndex(u => u.id === editTarget.value.id);
                if (idx !== -1) users.value[idx] = { ...users.value[idx], ...data };
                closeEditModal();
            } catch (err) {
                const code = err?.response?.data?.error;
                if (code === 'invalid_role') {
                    editError.value = 'Não é possível atribuir essa permissão.';
                } else if (code === 'cannot_manage_owner') {
                    editError.value = 'Não é possível editar o proprietário da conta.';
                } else {
                    editError.value = 'Erro ao salvar. Tente novamente.';
                }
            } finally {
                editSaving.value = false;
            }
        }

        async function toggleStatus(user) {
            try {
                togglingId.value = user.uuid;
                const { data } = await UserService.toggleStatus(user.uuid);
                const idx = users.value.findIndex(u => u.id === user.id);
                if (idx !== -1) users.value[idx] = { ...users.value[idx], ...data };
            } catch (err) {
                const code = err?.response?.data?.error;
                const title = code === 'cannot_manage_owner'
                    ? 'Não é possível desativar o proprietário.'
                    : 'Erro ao alterar status';
                Swal({ title, icon: 'error' });
            } finally {
                togglingId.value = null;
            }
        }

        async function confirmDelete(user) {
            const confirmed = await Swal({
                title: 'Excluir usuário?',
                text: `${user.name} será removido da conta e perderá acesso imediatamente.`,
                icon: 'warning',
                buttons: { cancel: 'Cancelar', confirm: { text: 'Excluir', value: true, className: 'swal-button--danger' } },
                dangerMode: true
            });

            if (!confirmed) return;

            try {
                deletingId.value = user.uuid;
                await UserService.softDelete(user.uuid);
                users.value = users.value.filter(u => u.id !== user.id);
            } catch (err) {
                const code = err?.response?.data?.error;
                const msg = code === 'cannot_manage_owner'
                    ? 'Não é possível excluir o proprietário.'
                    : 'Erro ao excluir usuário.';
                Swal({ title: msg, icon: 'error' });
            } finally {
                deletingId.value = null;
            }
        }

        function openResetPasswordModal(user) {
            resetTarget.value = user;
            resetPasswordForm.value.password = generateSecurePassword();
            resetPasswordDone.value = false;
            resetPasswordError.value = '';
            resetPasswordCopied.value = false;
            showResetPasswordValue.value = false;
            showResetPasswordModal.value = true;
        }

        function closeResetPasswordModal() {
            showResetPasswordModal.value = false;
            resetTarget.value = null;
        }

        function generateResetPassword() {
            resetPasswordForm.value.password = generateSecurePassword();
            resetPasswordCopied.value = false;
        }

        async function copyResetPassword() {
            try {
                await navigator.clipboard.writeText(resetPasswordForm.value.password);
                resetPasswordCopied.value = true;
                setTimeout(() => { resetPasswordCopied.value = false; }, 2000);
            } catch (e) { /* clipboard indisponível */ }
        }

        async function confirmResetPassword() {
            if (!resetTarget.value) return;
            resetPasswordError.value = '';
            if (resetPasswordErrorText.value) {
                resetPasswordError.value = resetPasswordErrorText.value;
                return;
            }

            try {
                resettingPassword.value = true;
                await UserService.resetPassword(resetTarget.value.uuid, {
                    password: resetPasswordForm.value.password
                });
                resetPasswordDone.value = true;
            } catch (err) {
                const code = err?.response?.data?.error;
                if (code === 'google_auth_user') {
                    resetPasswordError.value = 'Usuários com Google não possuem senha local.';
                } else if (code === 'cannot_manage_owner') {
                    resetPasswordError.value = 'Não é possível redefinir a senha do proprietário.';
                } else {
                    resetPasswordError.value = err?.response?.data?.text || 'Erro ao redefinir senha.';
                }
            } finally {
                resettingPassword.value = false;
            }
        }

        function initials(name) {
            if (!name) return '?';
            const parts = name.trim().split(' ');
            if (parts.length === 1) return parts[0][0].toUpperCase();
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }

        function roleLabel(role) {
            const map = { owner: 'Proprietário', admin: 'Admin', member: 'Membro' };
            return map[role] || role;
        }

        function roleBadgeClass(role) {
            if (role === 'owner') return 'bg-slate-100 text-slate-700';
            if (role === 'admin') return 'bg-blue-100 text-blue-700';
            return 'bg-gray-100 text-gray-500';
        }

        function isGoogleUser(user) {
            return user && (user.auth_provider === 'google' || user.is_google_user);
        }

        function canResetPassword(user) {
            return user && user.invite_status !== 'pending' && !isGoogleUser(user);
        }

        return {
            users,
            loading,
            togglingId,
            deletingId,
            currentUserUUID,
            canManage,
            showInviteModal,
            inviteSending,
            inviteError,
            inviteForm,
            showEditModal,
            editTarget,
            editForm,
            editSaving,
            editError,
            showResetPasswordModal,
            resetTarget,
            resetPasswordForm,
            resettingPassword,
            resetPasswordDone,
            resetPasswordError,
            resetPasswordCopied,
            showResetPasswordValue,
            resetPasswordErrorText,
            openInviteModal,
            closeInviteModal,
            sendInvite,
            openEditModal,
            closeEditModal,
            saveEdit,
            toggleStatus,
            confirmDelete,
            openResetPasswordModal,
            closeResetPasswordModal,
            generateResetPassword,
            copyResetPassword,
            confirmResetPassword,
            initials,
            roleLabel,
            roleBadgeClass,
            isGoogleUser,
            canResetPassword
        };
    }
}
</script>
